import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow, Typography, Tooltip, IconButton, Box } from "@mui/material";
import {
  Key as KeyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import Table from "../common/Table";
import UserAvatar from "../common/UserAvatar";
import SyncableChip from "../common/SyncableChip";
import SourceChip from "../common/SourceChip";
import type { User } from "../../types/user";
import { canViewProfile } from "../../types/user";
import { rawColor } from "../../constants/colors";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface UserTableProps {
  users: User[];
  currentUser: User | null;
  onResetPassword: (userId: string) => void;
  onEditRole: (user: User) => void;
  loading?: boolean;
  canMutate?: boolean;
}

const columns = [
  { id: "username", label: "Username" },
  { id: "name", label: "Name" },
  { id: "role", label: "Role" },
  { id: "statuses", label: "Statuses", align: "center" as const },
  { id: "source", label: "Source" },
  { id: "actions", label: "Actions", align: "center" as const },
];

const UserTable: React.FC<UserTableProps> = ({
  users,
  currentUser,
  onResetPassword,
  onEditRole,
  loading,
  canMutate = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hoveredRole, setHoveredRole] = useState<string | null>(null);
  const [filteredRole, setFilteredRole] = useState<string | null>(null);

  // Filter users based on selected role
  const filteredUsers = filteredRole
    ? users.filter((user) => {
        const userRole = user.role || "USER";
        const translatedRole = t(userRole as TranslationKeys);
        return translatedRole === filteredRole;
      })
    : users;

  const handleChipClick = (roleLabel: string) => {
    // If clicking the same role, clear the filter
    // Otherwise, set it as the filter
    if (filteredRole === roleLabel) {
      setFilteredRole(null);
    } else {
      setFilteredRole(roleLabel);
    }
  };

  return (
    <Table
      data={filteredUsers}
      columns={columns}
      loading={loading}
      showSearch={false}
      renderRow={(user) => {
        const userRole = user.role || "USER";
        const translatedRole = t(userRole as TranslationKeys);

        return (
          <TableRow key={user._id}>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UserAvatar user={user} size={28} />
                <Typography variant="body2">{user._id}</Typography>
              </Box>
            </TableCell>
            <TableCell>{user.name}</TableCell>
            <TableCell>
              <SyncableChip
                label={translatedRole}
                hoveredValue={hoveredRole}
                filteredValue={filteredRole}
                onMouseEnter={() => setHoveredRole(translatedRole)}
                onMouseLeave={() => setHoveredRole(null)}
                onClick={() => handleChipClick(translatedRole)}
              />
            </TableCell>
            <TableCell align="center">
              <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                <Tooltip
                  title={
                    user.isTempPassword
                      ? t("Temporary password" as TranslationKeys)
                      : t("Password set" as TranslationKeys)
                  }
                >
                  <KeyIcon
                    sx={{
                      color: user.isTempPassword
                        ? rawColor.status.error.red
                        : rawColor.status.active.green,
                      fontSize: "small",
                    }}
                  />
                </Tooltip>
                <Tooltip
                  title={
                    user.deactivated
                      ? t("User deactivated" as TranslationKeys)
                      : t("User active" as TranslationKeys)
                  }
                >
                  <DeleteIcon
                    sx={{
                      color: user.deactivated
                        ? rawColor.status.error.red
                        : rawColor.status.active.green,
                      fontSize: "small",
                    }}
                  />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell>
              <SourceChip source={user.initiallyCreatedFrom} />
            </TableCell>
            <TableCell align="center">
              <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                {currentUser &&
                user._id !== currentUser._id &&
                canViewProfile(currentUser, user) ? (
                  <Tooltip title={t("profile.viewProfile" as TranslationKeys)}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/profile/${user._id}`)}
                      data-testid={`${TestId.USERS.VIEW_PROFILE_BUTTON}-${user._id}`}
                    >
                      <PersonIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title={t("Change Role" as TranslationKeys)}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onEditRole(user)}
                      disabled={!canMutate}
                      data-testid={`${TestId.USERS.EDIT_ROLE_BUTTON}-${user._id}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("Reset Password" as TranslationKeys)}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onResetPassword(user._id)}
                      disabled={!canMutate}
                    >
                      <KeyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        );
      }}
    />
  );
};

export default UserTable;
